import { computed, effect, signal } from "@preact/signals-react";
import { GridColumn, GridColumnMenuFilter, GridColumnMenuSort } from "@progress/kendo-react-grid";
import createEngine from "@projectstorm/react-diagrams";
import { ulid } from "ulid";
import {
  allDatasets,
  userDatasets,
  userEntries,
  userTemplates,
} from "../Common/Store/CommonSignals";

//#region Template Data Signals

export function createEmptyTemplate() {
  return {
    id: ulid(),
    templateName: "",
    company: "",
    branch: "",
    area: "",
    department: "",
    sections: [
      {
        sectionId: ulid(),
        sectionName: "New Section",
        fields: [
          {
            fieldId: ulid(),
            fieldName: "",
            fieldType: "",
            properties: {},
          },
        ],
        properties: {},
      },
    ],
    owner: "",
    sharedPermission: [],
    coOwners: [],
    properties: {
      TemplateSync: false,
      DataConnections: [],
      StageSteps: [],
      ChildTemplate: "",
    },
  };
}

//Template Data
export const templateData = signal(getTemplateData());
//Gets the template data from local storage or returns an empty template
function getTemplateData() {
  const templateData = localStorage.getItem("TEMPLATE_DATA");
  return JSON.tryParse(templateData, createEmptyTemplate());
}

// Determines whether the template currently being edited has been persisted
// to the backend. Used to decide POST vs PUT and to gate UI that only makes
// sense for saved templates (history, favorite, etc.).
export const isTemplatePersisted = computed(() => {
  const id = templateData.value?.id;
  if (!id) return false;
  return userTemplates.value?.some((t) => t.id === id) ?? false;
});
//Saves the template data to local storage when it changes
effect(() => {
  localStorage.setItem("TEMPLATE_DATA", JSON.stringify(templateData.value));
});

//#endregion Template Data Signals

//#region Template Context Signals

export const templateDataVisible = signal(false);
export const templateInformationPaneVisible = signal(false);
export const templateInformationSearch = signal("");
export const templateHasUnsavedChanges = signal(false);
export const selectedTemplateMenuItem = signal();
export const selectedDataConnectionItem = signal();
export const showAddDataPopover = signal(false);
export const showEditPermissionsPopover = signal(false);
export const showConfigureDataPopover = signal(false);
export const showDataConnectionConfigurator = signal(false);
export const showDataConnectionColumnPopup = signal(false);
export const showColumnsConstructorWindow = signal(false);
export const showTemplatePreviewWindow = signal(false);
export const showTemplateHistoryWindow = signal(false);
export const showDatasetCreationWindow = signal(false);
export const showConfirmFieldNameEditDialog = signal(false);
export const dataConnectionColumnPopupOffset = signal();
export const expandedTreeItems = signal({});
export const expandedHierarchyItems = signal({});
export const expandedPermissionItems = signal({});
export const configureDataOffset = signal({ top: 0, left: 0 });
export const configureDataSelectCount = signal("All");
export const validationError = signal("");
export const templateEditMode = signal("Visual");

export const sectionFocusIndex = signal(0);
export const editSection = signal(false);
export const editSectionIndex = signal(0);
export const fieldFocusIndex = signal(0);
export const propertiesVisible = signal(false);
export const selectedSectionName = computed(() => {
  if (!templateData.value) return "";
  return templateData.value.sections[sectionFocusIndex.value]?.sectionName;
});
export const selectedFieldName = computed(() => {
  if (!templateData.value) return "";
  return templateData.value.sections[sectionFocusIndex.value]?.fields[fieldFocusIndex.value]
    ?.fieldName;
});
export const selectedFieldType = computed(() => {
  if (!templateData.value) return "";

  if (templateData.value?.sections?.[sectionFocusIndex.value]?.fields?.[fieldFocusIndex.value]) {
    return templateData.value?.sections?.[sectionFocusIndex.value]?.fields?.[fieldFocusIndex.value]
      ?.fieldType;
  }
  if (templateData.value?.sections?.[sectionFocusIndex.value]) {
    return "Section";
  }
  return "";
});
export const selectedFieldPropertyValues = computed(() => {
  if (!templateData.value) return "";
  if (typeof fieldFocusIndex.value !== "number")
    return templateData.value?.sections?.[sectionFocusIndex.value]?.properties;
  return templateData.value?.sections?.[sectionFocusIndex.value]?.fields?.[fieldFocusIndex.value]
    ?.properties;
});
export const isGalleryEditor = signal(false);
export const isTableEditor = signal(false);
export const isStageEditor = signal(false);
export const stageStepsMaxID = computed(() => {
  if (!templateData.value.properties.StageSteps) return 0;
  return Math.max(...templateData.value.properties.StageSteps.map((step) => step.id), 0);
});
//#endregion Template Context Signals

//#region Layout Signals
export const templatePaneSize = signal({
  0: { size: "15%" },
  1: { size: "70%" },
  2: { size: "15%" },
});

export const nestedPanes = computed(() => {
  return [
    {
      size: templateInformationPaneVisible.value ? templatePaneSize.value[0].size : "2%",
      resizable: templateInformationPaneVisible.value,
      scrollable: false,
    },
    {},
    {
      size: propertiesVisible.value ? templatePaneSize.value[2].size : "2%",
      resizable: propertiesVisible.value,
      scrollable: false,
    },
  ];
});

export const dataTreeView = computed(() => {
  return [
    {
      text: "My Templates",
      expanded: expandedTreeItems.value["My Templates"],
      title: true,
      items: userTemplates.value?.map((template) => {
        return { text: template.templateName, id: template.id, type: "Template" };
      }),
    },
    {
      text: "My Datasets",
      expanded: expandedTreeItems.value["My Datasets"],
      title: true,
      items: userDatasets.value
        ?.filter((dataset) => dataset.datasetType !== "Table")
        .map((dataset) => {
          return { text: dataset.datasetName, id: dataset.id, type: dataset.datasetType };
        }),
    },
  ];
});

export const templateTreeView = computed(() => {
  return templateData.value?.sections.map((section) => {
    return {
      id: section.sectionId,
      text: section.sectionName,
      properties: section.properties,
      expanded: expandedHierarchyItems.value[section.sectionName],
      type: "section",
      items: section.fields
        ?.map((field) => {
          return {
            id: field.fieldId,
            text: field.fieldName,
            type: field.fieldType,
            properties: field.properties,
          };
        })
        .filter((connection) => {
          return connection.text
            .toLowerCase()
            .includes(templateInformationSearch.value.toLowerCase());
        }),
    };
  });
});

export const templatePermissionTreeView = computed(() => {
  return [
    {
      text: "Owner",
      expanded: expandedPermissionItems.value["Owner"],
      items: [{ user: templateData.value?.owner }],
    },
    {
      text: "Co-Owners",
      expanded: expandedPermissionItems.value["Co-Owners"],
      items:
        templateData.value?.coOwners?.map(({ user, isUserGroup, userGroupId }) => {
          return { user: user, isUserGroup: isUserGroup, userGroupId: userGroupId };
        }) || [],
    },
    {
      text: "Shared Permissions",
      expanded: expandedPermissionItems.value["Shared Permissions"],
      items:
        templateData.value?.sharedPermission?.map(
          ({ entity, permission, isUserGroup, userGroupId }) => {
            return {
              user: entity,
              permission: permission,
              isUserGroup: isUserGroup,
              userGroupId: userGroupId,
            };
          }
        ) || [],
    },
  ];
});
//#endregion Layout Signals

//#region Template Data Connection Signals

export const templateDataConnections = computed(() => {
  return templateData.value?.properties?.DataConnections;
});

export const filteredDataConnections = computed(() => {
  return templateData.value?.properties?.DataConnections?.filter((connection) => {
    return connection.text.toLowerCase().includes(templateInformationSearch.value.toLowerCase());
  });
});

export const filteredEntriesByTemplateId = computed(() => {
  if (!selectedDataConnectionItem.value) return;
  if (selectedDataConnectionItem.value?.type !== "Template") return;

  let filteredEntries = userEntries.value?.[selectedDataConnectionItem.value.id];

  filteredEntries = filteredEntries?.map((entry) => {
    const flattenedFields = {};

    entry.sections.forEach((section) => {
      section.fields?.forEach((field) => {
        flattenedFields[field.fieldName] = field.value || field.values || field.properties.Values;
      });
    });

    const newEntry = { ...entry, ...flattenedFields };

    delete newEntry.sections;
    delete newEntry.templateId;
    delete newEntry.entryAuthor;
    delete newEntry.actionId;

    return newEntry;
  });

  filteredEntries?.sort((a, b) => b.createdDate - a.createdDate);

  return filteredEntries ?? [];
});

export const DataConnectionPreviewData = computed(() => {
  let connections = [];

  switch (selectedDataConnectionItem.value?.type) {
    case "Template":
      connections = filteredEntriesByTemplateId.value;
      break;
    case "List":
    case "Global Parameters":
      connections =
        allDatasets.value?.find((dataset) => {
          return dataset.id === selectedDataConnectionItem.value.id;
        })?.values ?? [];
      break;
    default:
      return;
  }
  return connections;
});

export const selectedDataConnectionColumns = computed(() => {
  const ColumnMenu = (props) => {
    return (
      <div>
        <GridColumnMenuSort {...props} />
        <GridColumnMenuFilter {...props} />
      </div>
    );
  };

  if (!selectedDataConnectionItem.value) return [];
  let columns = [];
  for (const key in selectedDataConnectionItem.value.columns) {
    if (selectedDataConnectionItem.value.columns[key].selected) {
      columns.push(
        <GridColumn
          field={key}
          title={key}
          columnMenu={ColumnMenu}
        />
      );
    }
  }

  return columns;
});

export const selectedDataConnectionGridSort = computed(() => {
  if (!selectedDataConnectionItem.value) return null;
  //index of selected data connection from templateData
  const index = templateData.value.properties.DataConnections.findIndex(
    (connection) => connection.id === selectedDataConnectionItem.value.id
  );
  if (index === -1) return null;
  return templateData.value.properties.DataConnections[index].sort || null;
});

export const selectedDataConnectionGridFilter = computed(() => {
  if (!selectedDataConnectionItem.value) return null;
  //index of selected data connection from templateData
  const index = templateData.value.properties.DataConnections.findIndex(
    (connection) => connection.id === selectedDataConnectionItem.value.id
  );
  if (index === -1) return null;
  return templateData.value.properties.DataConnections[index].filter || null;
});

//#endregion Template Data Connection Signals

//#region Template Automation signals
export const engine = signal(createEngine());
export const showAutomationsWindow = signal(false);
export const automationSplitterPaneSize = signal({
  0: { size: "15%" },
  1: { size: "70%" },
  2: { size: "15%" },
});
export const automationSplitterPanes = computed(() => {
  return [
    {
      size: automationSplitterPaneSize.value[0].size,
      resizable: true,
      scrollable: false,
    },
    {},
    {
      size: automationSplitterPaneSize.value[2].size,
      resizable: true,
      scrollable: false,
    },
  ];
});

export const selectedAutomationTreeViewItem = signal();
export const automationSessionData = signal([]);
export const selectedAutomationElementsCount = computed(() => {
  return (
    automationSessionData.value
      ?.find((automation) => automation.id === selectedAutomationTreeViewItem.value)
      ?.model?.getNodes()?.length || 0
  );
});
export const automationTreeViewItems = computed(() => {
  if (!automationSessionData.value) return [];

  return automationSessionData.value.map((automation) => {
    return {
      id: automation.id,
      text: automation.name,
      selected: selectedAutomationTreeViewItem.value === automation.id,
      checked: selectedAutomationTreeViewItem.value === automation.id,
    };
  });
});
export const selectedAutomation = computed(() => {
  return automationSessionData.value?.find(
    (automation) => automation.id === selectedAutomationTreeViewItem.value
  );
});
export const selectedAutomationNode = computed(() => {
  if (!selectedAutomation.value) return;
  return selectedAutomation.value.model?.getNodes().find((node) => node.options.selected);
});
export const selectedAutomationNodeProperties = computed(() => {
  if (!selectedAutomationNode.value) return;
  return selectedAutomationNode.value?.options?.extras?.properties;
});
//#endregion Template Automation signals
